import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Upload, ImageOff, X } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const MAX_IMAGE_BYTES = 1024 * 1024;

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ImageField({ value, onChange, testid }) {
  const inputRef = useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be under 1 MB.");
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      onChange(dataUrl);
    } catch {
      toast.error("Could not read the selected file.");
    } finally {
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-16 h-16 rounded-md border border-black/10 bg-[#FAFAFA] flex items-center justify-center overflow-hidden shrink-0">
        {value ? (
          <img src={value} alt="" className="w-full h-full object-contain" />
        ) : (
          <ImageOff className="h-5 w-5 text-[#A3A3A3]" />
        )}
      </div>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleFile}
          data-testid={testid}
        />
        <Button
          type="button"
          variant="outline"
          className="border-black/10 h-9"
          onClick={() => inputRef.current?.click()}
        >
          <Upload className="h-4 w-4 mr-1.5" />
          {value ? "Replace" : "Choose file"}
        </Button>
        {value ? (
          <Button
            type="button"
            variant="ghost"
            onClick={() => onChange("")}
            className="text-[#525252] hover:text-[#F50E02] h-9"
          >
            <X className="h-4 w-4 mr-1" />
            Remove
          </Button>
        ) : null}
      </div>
    </div>
  );
}

const MANDATORY = [
  "wheel_count_id",
  "vehicle_brand_id",
  "engine_stroke_type_id",
  "cylinder_category_id",
  "vehicle_number",
];

const FIELD_LABEL =
  "text-xs font-semibold uppercase tracking-wider text-[#525252] mb-1.5 block";
const INPUT_CLS = "h-10 bg-white border-black/10";

const MASTER_ENDPOINTS = {
  wheel_counts: "/master/wheel_counts",
  vehicle_brands: "/master/vehicle_brands",
  engine_stroke_types: "/master/engine_stroke_types",
  cylinder_categories: "/master/cylinder_categories",
};

export default function FuelAccessDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const lockedOutletId = user?.retail_outlet_id || "";
  const lockedOutletName = user?.retail_outlet_name || "";

  const [record, setRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [masters, setMasters] = useState({});
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [cylCount, setCylCount] = useState(0);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [recRes, cylRes, ...mRes] = await Promise.all([
          api.get(`/fuel-access/${id}`),
          api.get(`/fuel-access/${id}/cylinders`),
          ...Object.keys(MASTER_ENDPOINTS).map((k) =>
            api.get(MASTER_ENDPOINTS[k])
          ),
        ]);
        const rec = recRes.data;
        setRecord(rec);
        setCylCount((cylRes.data || []).length);
        const mData = {};
        Object.keys(MASTER_ENDPOINTS).forEach((k, idx) => {
          mData[k] = mRes[idx].data || [];
        });
        setMasters(mData);
        setForm({
          retail_outlet_id:
            lockedOutletId || rec.retail_outlet_id || "",
          wheel_count_id: rec.wheel_count_id || "",
          vehicle_brand_id: rec.vehicle_brand_id || "",
          engine_stroke_type_id: rec.engine_stroke_type_id || "",
          cylinder_category_id: rec.cylinder_category_id || "",
          vehicle_number: rec.vehicle_number || "",
          kit_installation_by: rec.kit_installation_by || "",
          kit_installed_date: rec.kit_installed_date || "",
          kit_serial_number: rec.kit_serial_number || "",
          kit_manufacturer_name: rec.kit_manufacturer_name || "",
          engine_number: rec.engine_number || "",
          chassis_number: rec.chassis_number || "",
          vehicle_manufacturer_date: rec.vehicle_manufacturer_date || "",
          owner_name: rec.owner_name || "",
          owner_phone_number: rec.owner_phone_number || "",
          permit_number: rec.permit_number || "",
          owner_address: rec.owner_address || "",
          rc_picture: rec.rc_picture || "",
          vehicle_picture: rec.vehicle_picture || "",
        });
      } catch (e) {
        toast.error(
          formatApiErrorDetail(e?.response?.data?.detail) ||
            "Failed to load fuel access record"
        );
        navigate("/fuel-access", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate, lockedOutletId]);

  const setField = (key, value) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const mandatoryMissing = () => {
    for (const f of MANDATORY) {
      const v = form[f];
      if (!v || (typeof v === "string" && !v.trim())) return true;
    }
    return false;
  };

  const isReadyToSave = () => !mandatoryMissing() && cylCount > 0;

  const handleSave = async () => {
    if (mandatoryMissing()) {
      toast.error("Please fill all mandatory fields (marked with *).");
      return;
    }
    if (cylCount === 0) {
      toast.error("At least one cylinder must be added before saving.");
      return;
    }
    setSaving(true);
    try {
      await api.put(`/fuel-access/${id}`, form);
      toast.success(`${record.temp_number} details saved`);
      navigate("/fuel-access");
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  const selectField = (key, labelText, masterKey, required = false) => {
    const options = masters[masterKey] || [];
    return (
      <div>
        <Label className={FIELD_LABEL}>
          {labelText}{" "}
          {required ? <span className="text-[#F50E02]">*</span> : null}
        </Label>
        <Select value={form[key] || ""} onValueChange={(v) => setField(key, v)}>
          <SelectTrigger className={INPUT_CLS} data-testid={`${key}-select`}>
            <SelectValue placeholder={`Choose ${labelText.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {options.length === 0 ? (
              <div className="px-3 py-2 text-xs text-[#525252]">
                No {labelText.toLowerCase()} available. Add some in Master Data first.
              </div>
            ) : (
              options.map((o) => (
                <SelectItem key={o.id} value={o.id}>
                  {o.name}
                </SelectItem>
              ))
            )}
          </SelectContent>
        </Select>
      </div>
    );
  };

  const textField = (
    key,
    labelText,
    placeholder,
    required = false,
    type = "text"
  ) => (
    <div>
      <Label className={FIELD_LABEL}>
        {labelText}{" "}
        {required ? <span className="text-[#F50E02]">*</span> : null}
      </Label>
      <Input
        type={type}
        value={form[key] || ""}
        onChange={(e) => setField(key, e.target.value)}
        placeholder={placeholder}
        className={INPUT_CLS}
        data-testid={`${key}-input`}
      />
    </div>
  );

  if (loading) {
    return (
      <div
        className="min-h-[60vh] flex items-center justify-center"
        data-testid="details-loading"
      >
        <Loader2 className="h-6 w-6 animate-spin text-[#F50E02]" />
      </div>
    );
  }

  return (
    <div data-testid="fuel-access-details-page">
      <header className="bg-white border-b border-black/5 px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate("/fuel-access")}
            className="h-9 w-9 inline-flex items-center justify-center rounded-md text-[#525252] hover:text-[#F50E02] hover:bg-[#F50E02]/10 transition-colors"
            aria-label="Back"
            data-testid="details-back-button"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-xl font-semibold text-[#0A0A0A] tracking-tight">
              {record?.status === "blank" ? "Add Details" : "Edit Details"}{" "}
              <span className="text-[#F50E02]">— {record?.temp_number}</span>
            </h1>
            <p className="text-xs text-[#525252] mt-0.5">
              Fill all mandatory fields <span className="text-[#F50E02]">*</span> and add at least one cylinder to save.
            </p>
          </div>
        </div>
      </header>

      <div className="p-8">
        <div className="bg-white border border-black/5 rounded-xl p-6 sm:p-8">
          {/* Cylinder requirement banner */}
          <div
            className={`mb-6 flex items-center gap-3 px-4 py-3 rounded-lg border text-sm ${
              cylCount > 0
                ? "bg-emerald-50 border-emerald-200 text-emerald-700"
                : "bg-[#F50E02]/5 border-[#F50E02]/20 text-[#F50E02]"
            }`}
            data-testid="cylinder-count-banner"
          >
            <span className="font-semibold">
              {cylCount > 0
                ? `${cylCount} cylinder${cylCount === 1 ? "" : "s"} added`
                : "No cylinders added yet"}
            </span>
            <span className="text-xs opacity-75">
              {cylCount > 0
                ? "Cylinder requirement met."
                : "— go to the View page to add at least one cylinder before saving."}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {/* QR Code (readonly) */}
            <div>
              <Label className={FIELD_LABEL}>
                QR Code <span className="text-[#F50E02]">*</span>
              </Label>
              <Input
                value={record?.temp_number || ""}
                readOnly
                className="h-10 bg-[#FAFAFA] border-black/10 text-[#0A0A0A] font-medium cursor-not-allowed"
                data-testid="qr-code-readonly"
              />
            </div>

            {lockedOutletId ? (
              <div>
                <Label className={FIELD_LABEL}>Retail Outlet</Label>
                <Input
                  value={lockedOutletName}
                  readOnly
                  disabled
                  className="h-10 bg-[#FAFAFA] border-black/10 text-[#0A0A0A] font-medium cursor-not-allowed disabled:opacity-100"
                  data-testid="retail_outlet_id-locked"
                />
              </div>
            ) : (
              selectField("retail_outlet_id", "Retail Outlet", "retail_outlets")
            )}

            {selectField("wheel_count_id", "Wheeler", "wheel_counts", true)}
            {selectField("vehicle_brand_id", "Vehicle Brand", "vehicle_brands", true)}
            {selectField(
              "engine_stroke_type_id",
              "Engine Stroke Type",
              "engine_stroke_types",
              true
            )}
            {selectField(
              "cylinder_category_id",
              "Category of Cylinder",
              "cylinder_categories",
              true
            )}
            {textField("vehicle_number", "Vehicle Number", "Enter Vehicle Number", true)}

            {textField("kit_installation_by", "Kit Installation By", "Enter Kit Installation By")}
            {textField("kit_installed_date", "Kit Installed Date", "", false, "date")}

            {textField("kit_serial_number", "Kit Serial Number", "Enter Kit Serial Number")}
            {textField("kit_manufacturer_name", "Kit Manufacturer Name", "Enter Kit Manufacturer Name")}

            {textField("engine_number", "Engine Number", "Enter Engine Number")}
            {textField("chassis_number", "Chassis Number", "Enter Chassis Number")}

            {textField("vehicle_manufacturer_date", "Vehicle Manufacturer Date", "", false, "date")}
            {textField("owner_name", "Owner Name", "Enter Owner Name")}

            {textField("owner_phone_number", "Owner Phone Number", "Enter Owner Phone Number")}
            {textField("permit_number", "Permit Number", "Enter Permit Number")}

            <div className="sm:col-span-2">
              <Label className={FIELD_LABEL}>Owner Address</Label>
              <Textarea
                value={form.owner_address || ""}
                onChange={(e) => setField("owner_address", e.target.value)}
                placeholder="Enter Owner Address"
                rows={3}
                className="bg-white border-black/10"
                data-testid="owner_address-input"
              />
            </div>

            <div>
              <Label className={FIELD_LABEL}>RC Picture</Label>
              <ImageField
                value={form.rc_picture || ""}
                onChange={(v) => setField("rc_picture", v)}
                testid="rc_picture-input"
              />
            </div>
            <div>
              <Label className={FIELD_LABEL}>Vehicle Picture</Label>
              <ImageField
                value={form.vehicle_picture || ""}
                onChange={(v) => setField("vehicle_picture", v)}
                testid="vehicle_picture-input"
              />
            </div>
          </div>

          <div className="mt-8 flex items-center justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/fuel-access")}
              className="border-black/10"
              data-testid="details-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !isReadyToSave()}
              className="bg-[#F50E02] hover:bg-[#D80C02] text-white"
              data-testid="details-save"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Save Details
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
